import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ordersQueries } from '../api/orders-queries';
import type { OrderActorRole, OrderOptionsFilter } from '../api/orders-api';
import type { OffersMode } from '../lib/offers';
import { LIVE_TRACKING_STAGES } from './stages';
import type { OrderListFilter } from './list-params';

/** Paginated, filtered orders list. Keeps the previous page while loading. */
export function useOrdersList(filter: OrderListFilter) {
  return useQuery({
    ...ordersQueries.list(filter),
    placeholderData: (prev) => prev,
  });
}

/** Infinite, searchable vendor options for the orders entity filter. */
export function useOrderVendorOptions(
  filter: Omit<OrderOptionsFilter, 'skip'>,
  enabled: boolean,
) {
  return useInfiniteQuery(ordersQueries.vendorOptions(filter, enabled));
}

/** Infinite, searchable courier options for the orders courier filter. */
export function useOrderCourierOptions(
  filter: Omit<OrderOptionsFilter, 'skip'>,
  enabled: boolean,
) {
  return useInfiniteQuery(ordersQueries.courierOptions(filter, enabled));
}

/** Profile of the last updater, fetched lazily (on hover) and cached forever. */
export function useOrderActor(
  role: OrderActorRole | null,
  id: string | number | null | undefined,
  enabled: boolean,
) {
  return useQuery(
    ordersQueries.actor(role ?? 'USER', id ?? '', enabled && role != null && id != null),
  );
}

/** The night alarm's in-search poll over a rolling window (see `ordersQueries.searchAlarm`). */
export function useSearchAlarmOrders(
  window: { from_date: string; till_date: string },
  enabled: boolean,
) {
  return useQuery(ordersQueries.searchAlarm(window, enabled));
}

/** Single order detail by id. */
export function useOrderDetail(id: string | number) {
  return useQuery(ordersQueries.detail(id));
}

/** Courier-assignment offers + summary for an order (the Assignment tab). */
export function useOrderAssignment(id: string | number) {
  return useQuery(ordersQueries.assignment(id));
}

/** Per-courier offers + legend for the offers visualization modal, per mode. */
export function useOrderOffers(id: string | number, mode: OffersMode, enabled = true) {
  return useQuery(ordersQueries.offers(id, mode, enabled));
}

/**
 * Order change-log feed (status history). The single-order payload doesn't
 * inline these, so the timeline fetches them from the dedicated logs endpoint.
 */
export function useOrderLogs(id: string | number) {
  return useQuery(ordersQueries.logs(id));
}

/** Whether an order's stage has a live courier worth polling (7–14). */
export function isLiveTrackingStage(stage: number | null | undefined): boolean {
  return stage != null && (LIVE_TRACKING_STAGES as readonly number[]).includes(stage);
}

/**
 * Live courier location, polled every 5 s — but only when the stage is in the
 * live-tracking window (7–14) and we have both ids. Otherwise idle.
 */
export function useCourierLocation(
  orderId: string | number,
  courierId: string | number | null | undefined,
  stage: number | null | undefined,
) {
  const enabled = Boolean(orderId) && Boolean(courierId) && isLiveTrackingStage(stage);
  return useQuery(ordersQueries.courierLocation(orderId, courierId ?? '', enabled));
}

/** Live courier battery+location, same gating as `useCourierLocation`. */
export function useCourierBatteryLocation(
  orderId: string | number,
  courierId: string | number | null | undefined,
  stage: number | null | undefined,
) {
  const enabled = Boolean(orderId) && Boolean(courierId) && isLiveTrackingStage(stage);
  return useQuery(ordersQueries.batteryLocation(orderId, courierId ?? '', enabled));
}
